import type { ReactNode } from 'react';
import { useEscapeClose } from '../../hooks/useEscapeClose';

/**
 * The popup a table session's masthead opens — the parity surface for what the frame cannot show
 * at this viewport, in the same backdrop-to-dismiss overlay the pre-023 /problems mobile layout
 * used. The ⓘ details popup was the first caller; /problems' difficulty range is the second.
 *
 * Shell only: the rows are the caller's, because what a session hides at a given width is exactly
 * what its own chrome cannot afford there and that differs per surface. What is shared is the
 * overlay, the dialog chrome, the close affordance and the `.details-popup-overlay` hook that
 * index.css re-centres on landscape phones.
 */
export function DetailsPopup({
  title,
  ariaLabel,
  onClose,
  children,
  screens,
  testId = 'details-popup',
}: {
  /** The visible heading. */
  title: string;
  /** Names the dialog for screen readers. Defaults to the visible heading. */
  ariaLabel?: string;
  onClose: () => void;
  /** The fact rows. */
  children: ReactNode;
  /**
   * Which viewports this popup serves, as display utilities on the overlay. The default is the
   * phone set — the widths where the masthead's compact info row stands in for the status line.
   * A caller whose inline control is suppressed on a DIFFERENT set passes its own complement
   * (/problems' difficulty popup passes `DIFFICULTY_RANGE_COMPACT`, the clause-for-clause negation
   * of the gate on the frame's controls row), so the popup and the thing it replaces are never both
   * on screen.
   *
   * Belt and braces either way: the trigger that sets the open state is gated the same way, so
   * this only bites when the viewport is resized with the popup already open.
   */
  screens?: string;
  /**
   * Test hook on the dialog. Defaults to the frozen `details-popup` the e2e suites anchor on; a
   * second popup on the same page must pass its own so a selector cannot match whichever is open.
   */
  testId?: string;
}) {
  // Esc closes it, via the shared hook — constitution VI "Modal Esc dismissal", which wants one
  // mechanism site-wide rather than a local handler per dialog. `true` because the popup is
  // mounted only while open: every caller wraps it in its own `{showX && ...}`, so there is no
  // closed-but-rendered state for the hook to guard, and an `isOpen` prop would be a second
  // source of truth for something the mount already says.
  //
  // Fixing it HERE fixes every caller at once: the ⓘ session details on Counting practice,
  // module practice and /problems all render through this shell, as does /problems' difficulty
  // range popup. The backdrop click and the ✕ were the only ways out before, neither of them
  // reachable from the keyboard.
  useEscapeClose(onClose, true);

  return (
    <div
      className={`details-popup-overlay fixed inset-0 z-40 flex items-start justify-center bg-black/30 px-4 pt-20 ${screens ?? 'md:hidden narrow:flex'}`}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel ?? title}
        className="relative w-full max-w-sm rounded-lg bg-card p-4 pr-10 shadow-lg"
        onClick={(e) => e.stopPropagation()}
        data-testid={testId}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-1 right-1 p-2 text-base font-bold leading-none text-ink-soft hover:text-ink"
          aria-label="Close"
        >
          ✕
        </button>
        <h3 className="mb-2 text-sm font-semibold text-ink">{title}</h3>
        <div className="space-y-1 text-sm text-ink">{children}</div>
      </div>
    </div>
  );
}
